import { useMatchedPatients } from '@ctw/shared/api/fhir/patient-helper';
import { withErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { LegacyTable } from '@ctw/shared/components/tables/legacy-table';
import { canonicalContactColumns } from '@ctw/shared/content/demographics/helpers/columns';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { canonicalizeContactInfo } from './helpers/canonicalization';

export interface PatientContactInformationProps {
  className?: ClassName;
}

export const PatientContactInformation = withErrorBoundary({
  boundaryName: 'PatientContactInformation',
  Component: ({ className }: PatientContactInformationProps) => {
    const { requestContext } = useCTW();
    const matchedPatients = useMatchedPatients({
      includeCurrentBuilder: true,
    });

    if (!matchedPatients.data) {
      return <LoadingSpinner centered={true} message="Loading..." />;
    }

    const canonicalContactInfo = canonicalizeContactInfo(
      requestContext.builderId,
      matchedPatients.data,
    );

    return (
      <div className={twx(className, 'pt-5')}>
        <div className={tw`flex flex-col justify-between gap-5`}>
          <div data-testid="phone-numbers">
            <LegacyTable
              title="Phone Numbers"
              titleVisuallyHidden={true}
              className={tw`!border rounded-md border-background-subtle`}
              records={canonicalContactInfo.phones}
              columns={canonicalContactColumns('Phone Number')}
              emptyMessage="No phone numbers found for this patient."
            />
          </div>
          <div data-testid="email-addresses">
            <LegacyTable
              title="Email Addresses"
              titleVisuallyHidden={true}
              className={tw`!border rounded-md border-background-subtle`}
              records={canonicalContactInfo.emails}
              columns={canonicalContactColumns('Email Address')}
              emptyMessage="No email addresses found for this patient."
            />
          </div>
          <div data-testid="addresses">
            <LegacyTable
              title="Addresses"
              titleVisuallyHidden={true}
              className={tw`!border rounded-md border-background-subtle`}
              records={canonicalContactInfo.addresses}
              columns={canonicalContactColumns('Address')}
              emptyMessage="No addresses found for this patient."
            />
          </div>
        </div>
      </div>
    );
  },
});
